import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import api from '../api/api';
import Alert from './Alert';
import Icon from './Icon';
import {
    IMAGE_TYPES, MAX_IMAGE_BYTES, MAX_VIDEO_BYTES, VIDEO_TYPES, PUBLISH_PLATFORMS,
    formatBytes, formatWhen, jpegName, normalizeCapabilities, normalizeJobs, platformIcon, platformIdFor, platformLabel,
    plannedDate, toJpeg, toLocalInput,
} from '../utils/publishing';
import './publish.css';

// "Publish" on a plan post: pick connected accounts, check the text and media,
// then post now or schedule it (POST /publish). Rendered in <body>.

const PLATFORM_ORDER = Object.keys(PUBLISH_PLATFORMS);
const ACCEPT = [...IMAGE_TYPES, ...VIDEO_TYPES].join(',');
const MAX_FILES = 10;
// Room the tracked short link takes when it's added to the text (a blank line and the URL)
const LINK_ROOM = 30;

const toText = (value) => (typeof value === 'string' ? value : '');

// Facebook Pages and LinkedIn go by name, the rest by @handle
const AT_HANDLES = ['instagram', 'threads', 'tiktok', 'x', 'youtube', 'pinterest'];
const accountName = (account) => {
    const name = String(account.handle || account.displayName || '').replace(/^@+/, '');
    if (!name) return platformLabel(account.platform);
    return AT_HANDLES.includes(account.platform) ? `@${name}` : name;
};

const prefillText = (post) => {
    const draft = post.draft;
    if (draft) return [draft.hook, toText(draft.caption).trim(), (draft.hashtags || []).join(' ')].filter(Boolean).join('\n\n');
    return [post.hook, post.idea].map(item => toText(item).trim()).filter(Boolean).join('\n\n');
};

const browserZone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
        return '';
    }
};

// A quarter past the next hour or so: the default when the planned time has gone
const soon = () => {
    const date = new Date(Date.now() + 60 * 60 * 1000);
    date.setMinutes(Math.ceil(date.getMinutes() / 15) * 15, 0, 0);
    return date;
};

const listNames = (names) => (names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

const errorText = (error, fallback) => {
    const message = error?.response?.data?.error;
    return (typeof message === 'string' && message.trim() && error.response.status < 500) ? message : fallback;
};

const FOCUSABLE = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

let mediaKey = 0;

// initialText / initialFiles: what a Create screen's Review step made (the text, and File objects
// to upload). Without a plan post (no post.id / weekStart) it publishes without linking to the plan.
const PublishModal = ({ post, weekStart, linkDestination = '', initialText, initialFiles, onClose, onPublished }) => {
    const titleId = useId();
    const textId = useId();
    const fileId = useId();
    const modalRef = useRef(null);
    const fileRef = useRef(null);
    const mounted = useRef(true);
    const mediaRef = useRef([]);
    const postPlatform = platformIdFor(post.platform);
    // Posts from the plan name their platform; a post made outside the plan only matches one
    const planNote = post.id ? 'Planned platform' : 'Matches this post';

    const planned = useMemo(() => plannedDate(weekStart, post.day, post.time), [weekStart, post.day, post.time]);
    const plannedInFuture = Boolean(planned) && planned.getTime() > Date.now() + 5 * 60 * 1000;

    const [load, setLoad] = useState({ status: 'loading', capabilities: {}, accounts: [] });
    const [selected, setSelected] = useState([]);
    const [text, setText] = useState(() => (typeof initialText === 'string' ? initialText : prefillText(post)));
    const [media, setMedia] = useState([]);
    const [mediaNotice, setMediaNotice] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [boards, setBoards] = useState({}); // { accountId: { status, list, error } }
    const [boardId, setBoardId] = useState('');
    const [pinTitle, setPinTitle] = useState(() => toText(post.draft?.hook || post.hook || post.theme).slice(0, 100));
    const canAddLink = Boolean(linkDestination || post.shortUrl);
    const [addLink, setAddLink] = useState(canAddLink);
    const [when, setWhen] = useState(plannedInFuture ? 'schedule' : 'now');
    const [scheduleValue, setScheduleValue] = useState(() => toLocalInput(plannedInFuture ? planned : soon()));
    const [submitting, setSubmitting] = useState(false);
    const [tried, setTried] = useState(false);
    const [error, setError] = useState('');
    const [accountErrors, setAccountErrors] = useState({});

    mediaRef.current = media;
    const uploading = media.some(item => item.status === 'preparing' || item.status === 'uploading');
    const busy = uploading || submitting;

    /* ---------- Loading accounts and capabilities ---------- */

    const loadAll = useCallback(async () => {
        setLoad(previous => ({ ...previous, status: 'loading' }));
        try {
            const [capabilities, social] = await Promise.all([api.getPublishCapabilities(), api.getSocialAccounts()]);
            if (!mounted.current) return;
            const caps = normalizeCapabilities(capabilities);
            const accounts = (Array.isArray(social?.accounts) ? social.accounts : [])
                .filter(account => account && account.kind === 'oauth' && account.id);
            setLoad({ status: 'ready', capabilities: caps, accounts });
            // The post's own platform starts selected, when it's connected and can post
            const ready = accounts.filter(account => account.status === 'connected' && caps[account.platform]?.phase === 1);
            const matching = ready.filter(account => account.platform === postPlatform);
            setSelected((matching.length ? matching : ready.length === 1 ? ready : []).map(account => account.id));
        } catch (e) {
            if (!mounted.current) return;
            setLoad(previous => ({ ...previous, status: 'error' }));
        }
    }, [postPlatform]);

    useEffect(() => {
        mounted.current = true;
        loadAll();
        return () => {
            mounted.current = false;
        };
    }, [loadAll]);

    // Previews and unfinished uploads go when the modal closes
    useEffect(() => () => {
        mediaRef.current.forEach(item => {
            item.controller?.abort();
            if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        });
    }, []);

    const { capabilities, accounts } = load;
    const capsFor = (platform) => capabilities[platform] || {};
    const isPhase1 = (platform) => capsFor(platform).phase === 1;

    // The post's platform first, then accounts that can post, then by platform
    const sortedAccounts = useMemo(() => {
        const ready = (account) => account.status === 'connected' && capabilities[account.platform]?.phase === 1;
        return [...accounts].sort((a, b) => (
            ((b.platform === postPlatform) - (a.platform === postPlatform))
            || (ready(b) - ready(a))
            || (PLATFORM_ORDER.indexOf(a.platform) - PLATFORM_ORDER.indexOf(b.platform))
        ));
    }, [accounts, postPlatform, capabilities]);

    const usable = sortedAccounts.filter(account => account.status === 'connected' && isPhase1(account.platform));
    // Platforms that can't post yet, besides the accounts listed for them
    const comingSoon = Object.keys(capabilities).filter(id => !isPhase1(id) && !accounts.some(account => account.platform === id));
    const selectedAccounts = sortedAccounts.filter(account => selected.includes(account.id));
    const selectedPlatforms = [...new Set(selectedAccounts.map(account => account.platform))];
    const pinterestAccount = selectedAccounts.find(account => account.platform === 'pinterest');

    /* ---------- Pinterest boards ---------- */

    const loadBoards = useCallback(async (accountId) => {
        setBoards(previous => ({ ...previous, [accountId]: { status: 'loading', list: [] } }));
        try {
            const list = (await api.getPinterestBoards(accountId) || [])
                .filter(board => board && board.id)
                .map(board => ({ id: String(board.id), name: toText(board.name) || String(board.id) }));
            if (!mounted.current) return;
            setBoards(previous => ({ ...previous, [accountId]: { status: 'ready', list } }));
            setBoardId(current => (list.some(board => board.id === current) ? current : list[0]?.id || ''));
        } catch (e) {
            if (!mounted.current) return;
            setBoards(previous => ({ ...previous, [accountId]: { status: 'error', list: [], error: errorText(e, "Couldn't load your boards.") } }));
        }
    }, []);

    useEffect(() => {
        if (pinterestAccount && !boards[pinterestAccount.id]) loadBoards(pinterestAccount.id);
    }, [pinterestAccount, boards, loadBoards]);

    /* ---------- Media ---------- */

    const updateItem = (key, change) => setMedia(previous => previous.map(item => (item.key === key ? { ...item, ...change } : item)));

    const uploadItem = async (item, file) => {
        const controller = new AbortController();
        updateItem(item.key, { controller });
        try {
            let blob = file;
            let name = file.name;
            if (item.kind === 'image') {
                const converted = await toJpeg(file);
                blob = converted.blob;
                name = jpegName(file.name);
                if (blob.size > MAX_IMAGE_BYTES) throw new Error(`Still ${formatBytes(blob.size)} as a JPEG, over the 8 MB limit. Use a smaller image.`);
            }
            if (controller.signal.aborted || !mounted.current) return;
            const mimeType = item.kind === 'image' ? 'image/jpeg' : file.type;
            updateItem(item.key, { status: 'uploading', progress: 0, bytes: blob.size });
            const upload = await api.createMediaUpload({ name, mimeType, bytes: blob.size });
            if (controller.signal.aborted || !mounted.current) return;
            await api.uploadMediaFile(upload, blob, {
                signal: controller.signal,
                onProgress: (progress) => mounted.current && updateItem(item.key, { progress }),
            });
            const { asset } = await api.completeMediaUpload(upload.assetId);
            if (!mounted.current) return;
            if (controller.signal.aborted) {
                api.deleteMedia(upload.assetId).catch(() => {});
                return;
            }
            updateItem(item.key, { status: 'done', progress: 1, asset: { ...asset, id: asset?.id || upload.assetId }, controller: null });
        } catch (e) {
            if (e?.aborted || controller.signal.aborted || !mounted.current) return;
            updateItem(item.key, {
                status: 'error',
                controller: null,
                error: e?.response ? errorText(e, "The server didn't accept it.") : (e?.message && !/^Upload failed/.test(e.message) ? e.message : "The upload didn't finish. Check your connection."),
            });
        }
    };

    const addFiles = (fileList) => {
        const files = [...(fileList || [])];
        if (!files.length) return;
        const current = mediaRef.current.filter(item => item.status !== 'error');
        const notes = [];
        const accepted = [];
        let hasVideo = current.some(item => item.kind === 'video');
        let count = current.length;
        files.forEach(file => {
            const kind = IMAGE_TYPES.includes(file.type) ? 'image' : VIDEO_TYPES.includes(file.type) ? 'video' : '';
            if (!kind) {
                notes.push(`${file.name} isn't a JPEG, PNG, WebP, MP4 or MOV file.`);
            } else if (kind === 'video' && file.size > MAX_VIDEO_BYTES) {
                notes.push(`${file.name} is ${formatBytes(file.size)}. Videos can be up to 300 MB.`);
            } else if (kind === 'image' && file.size > 40 * 1024 * 1024) {
                notes.push(`${file.name} is ${formatBytes(file.size)}. Use an image under 40 MB.`);
            } else if (hasVideo || (kind === 'video' && count > 0)) {
                notes.push('A post takes one video, or up to 10 images. Remove what\'s there to switch.');
            } else if (count >= MAX_FILES) {
                notes.push(`A post takes up to ${MAX_FILES} images.`);
            } else {
                mediaKey += 1;
                accepted.push({ key: `m${mediaKey}`, file, name: file.name, kind, previewUrl: URL.createObjectURL(file), status: 'preparing', progress: 0 });
                count += 1;
                if (kind === 'video') hasVideo = true;
            }
        });
        setMediaNotice([...new Set(notes)].join(' '));
        if (!accepted.length) return;
        setMedia(previous => [...previous, ...accepted.map(({ file, ...item }) => item)]);
        accepted.forEach(({ file, ...item }) => uploadItem(item, file));
    };

    const removeItem = (item) => {
        item.controller?.abort();
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        if (item.asset?.id) api.deleteMedia(item.asset.id).catch(() => {});
        setMedia(previous => previous.filter(other => other.key !== item.key));
        setMediaNotice('');
        // The tile's button is gone, so focus goes to the add button
        window.requestAnimationFrame(() => modalRef.current?.querySelector('.ls-publish-add')?.focus());
    };

    const moveItem = (index) => setMedia(previous => {
        const next = [...previous];
        [next[index - 1], next[index]] = [next[index], next[index - 1]];
        return next;
    });

    // Media made in the Create flow starts uploading as the modal opens (once, also under StrictMode)
    const addedInitial = useRef(false);
    useEffect(() => {
        if (addedInitial.current || !initialFiles?.length) return;
        addedInitial.current = true;
        addFiles(initialFiles);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const readyMedia = media.filter(item => item.status === 'done');
    const imageCount = readyMedia.filter(item => item.kind === 'image').length;
    const hasVideoMedia = media.some(item => item.kind === 'video');

    /* ---------- Checks ---------- */

    const linkFor = (platform) => addLink && canAddLink && platform !== 'instagram' && capsFor(platform).links !== false;
    const lengthFor = (platform) => text.trim().length + (linkFor(platform) ? (post.shortUrl ? post.shortUrl.length + 2 : LINK_ROOM) : 0);

    const needsMediaNames = selectedPlatforms.filter(platform => capsFor(platform).needsMedia && !readyMedia.length && !uploading).map(platformLabel);
    const noVideoNames = hasVideoMedia ? selectedPlatforms.filter(platform => !capsFor(platform).video).map(platformLabel) : [];
    const tooManyImages = selectedPlatforms.filter(platform => capsFor(platform).maxImages && imageCount > capsFor(platform).maxImages);

    const scheduledDate = when === 'schedule' ? new Date(scheduleValue) : null;
    const scheduleInvalid = when === 'schedule' && (!scheduleValue || Number.isNaN(scheduledDate.getTime()));
    const schedulePast = when === 'schedule' && !scheduleInvalid && scheduledDate.getTime() < Date.now() + 60 * 1000;

    const issues = [];
    if (!selected.length) issues.push('Pick at least one account to post to.');
    if (!text.trim() && !readyMedia.length) issues.push('Write the post, or add an image or video.');
    selectedPlatforms.forEach(platform => {
        const limit = capsFor(platform).textLimit;
        if (limit && lengthFor(platform) > limit) issues.push(`${platformLabel(platform)} takes up to ${limit.toLocaleString()} characters. Shorten the text by ${(lengthFor(platform) - limit).toLocaleString()}.`);
    });
    if (needsMediaNames.length) issues.push(`${listNames(needsMediaNames)} ${needsMediaNames.length === 1 ? 'needs' : 'need'} an image or video.`);
    if (noVideoNames.length) issues.push(`${listNames(noVideoNames)} can't post video yet. Use images, or unselect ${noVideoNames.length === 1 ? 'it' : 'them'}.`);
    tooManyImages.forEach(platform => issues.push(`${platformLabel(platform)} takes up to ${capsFor(platform).maxImages} images.`));
    if (pinterestAccount && !boardId) issues.push('Pick a Pinterest board.');
    if (media.some(item => item.status === 'error')) issues.push('Remove the files that didn\'t upload, or add them again.');
    if (scheduleInvalid) issues.push('Pick a date and time to schedule it.');
    if (schedulePast) issues.push('That time has passed. Pick a time in the future, or post now.');

    /* ---------- Submit ---------- */

    // The problems show above the buttons; on a long form, bring them into view
    const showAlert = () => window.requestAnimationFrame(() => {
        modalRef.current?.querySelector('.ls-publish-alert')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });

    const submit = async () => {
        setTried(true);
        setError('');
        setAccountErrors({});
        if (issues.length || busy) {
            showAlert();
            return;
        }
        setSubmitting(true);
        try {
            const result = await api.publish({
                accountIds: selected,
                text: text.trim(),
                mediaIds: readyMedia.map(item => item.asset.id),
                scheduledAt: when === 'schedule' ? scheduledDate.toISOString() : null,
                planWeek: post.id ? weekStart : undefined,
                planPostId: post.id || undefined,
                options: {
                    ...(pinterestAccount && { boardId, title: pinTitle.trim() || undefined }),
                    addTrackedLink: Boolean(addLink && canAddLink),
                },
            });
            if (!mounted.current) return;
            onPublished?.(normalizeJobs(result?.jobs), {
                scheduledAt: when === 'schedule' ? scheduledDate.toISOString() : null,
                // e.g. "No tracked link was added: …"
                warnings: (Array.isArray(result?.warnings) ? result.warnings : []).filter(item => typeof item === 'string' && item),
            });
        } catch (e) {
            if (!mounted.current) return;
            const details = Array.isArray(e?.response?.data?.details) ? e.response.data.details : [];
            const byAccount = details.reduce((acc, item) => (item && item.accountId
                ? { ...acc, [item.accountId]: (Array.isArray(item.errors) ? item.errors : [item.errors]).filter(message => typeof message === 'string' && message) }
                : acc), {});
            setAccountErrors(byAccount);
            const unknown = details.filter(item => item && !accounts.some(account => account.id === item.accountId))
                .flatMap(item => (Array.isArray(item.errors) ? item.errors : [])).filter(message => typeof message === 'string');
            const fallback = e?.response ? "Couldn't publish. Nothing was posted, so please try again." : "Couldn't reach the server. Nothing was posted. Check your connection and try again.";
            setError([Object.keys(byAccount).length ? 'Fix the problems marked on the accounts above.' : errorText(e, fallback), ...unknown].join(' '));
            setSubmitting(false);
            showAlert();
        }
    };

    /* ---------- Closing and keyboard ---------- */

    const requestClose = useCallback(() => {
        if (submitting) return;
        if (uploading && !window.confirm('Stop uploading and close? The files that are uploading are dropped.')) return;
        // Files uploaded for a post that wasn't published aren't kept
        mediaRef.current.forEach(item => item.asset?.id && api.deleteMedia(item.asset.id).catch(() => {}));
        onClose();
    }, [submitting, uploading, onClose]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && !busy) {
                e.preventDefault();
                requestClose();
            }
            // Focus stays in the modal: Tab from the last control goes back to the first
            if (e.key === 'Tab' && modalRef.current) {
                const items = [...modalRef.current.querySelectorAll(FOCUSABLE)].filter(item => item.offsetParent !== null);
                if (!items.length) return;
                const first = items[0];
                const last = items[items.length - 1];
                if (e.shiftKey && (document.activeElement === first || !modalRef.current.contains(document.activeElement))) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [busy, requestClose]);

    // The first field gets focus once the accounts are in (the text when there's nothing to pick)
    const focused = useRef(false);
    useEffect(() => {
        if (focused.current) return;
        if (load.status === 'loading') {
            modalRef.current?.focus();
            return;
        }
        focused.current = true;
        const target = modalRef.current?.querySelector('.ls-publish-account input:not(:disabled)')
            || document.getElementById(textId)
            || modalRef.current?.querySelector('.ls-publish-empty-btn');
        target?.focus();
    }, [load.status, textId]);

    const toggleAccount = (id, on) => {
        setSelected(previous => (on ? [...previous, id] : previous.filter(item => item !== id)));
        setAccountErrors(previous => {
            const { [id]: removed, ...rest } = previous;
            return rest;
        });
    };

    /* ---------- Rendering ---------- */

    const renderAccounts = () => {
        if (load.status === 'loading') {
            return (
                <ul className="ls-publish-accounts" aria-hidden="true">
                    {[0, 1].map(index => (
                        <li key={index} className="ls-publish-account is-skeleton">
                            <span className="ls-skeleton ls-publish-glyph" />
                            <span className="ls-publish-account-text">
                                <span className="ls-skeleton-text" style={{ width: 120 }} />
                                <span className="ls-skeleton-text" style={{ width: 70 }} />
                            </span>
                        </li>
                    ))}
                </ul>
            );
        }
        if (load.status === 'error') {
            return (
                <Alert severity="error" action={<button type="button" className="ls-btn ls-btn-secondary ls-publish-retry" onClick={loadAll}>Try again</button>}>
                    Couldn't load your accounts. Check your connection, then try again.
                </Alert>
            );
        }
        if (!usable.length) {
            return (
                <div className="ls-publish-empty">
                    <Icon name="hub" size={24} className="ls-publish-empty-icon" />
                    <div>
                        <div className="ls-publish-empty-title">Connect an account to publish</div>
                        <p className="ls-publish-empty-text">
                            {accounts.length
                                ? 'None of your accounts can post right now: they need to be reconnected, or their platform is coming soon. '
                                : 'Sign in to Instagram, Facebook, Threads, LinkedIn or Pinterest once, and light speed can post there for you. '}
                            Accounts linked by handle only can't post.
                        </p>
                        <Link className="ls-btn ls-btn-secondary ls-publish-empty-btn" to="/dashboard/channels">
                            <Icon name="link" size={18} />Go to Social accounts
                        </Link>
                    </div>
                </div>
            );
        }
        return (
            <ul className="ls-publish-accounts">
                {sortedAccounts.map(account => {
                    const phase1 = isPhase1(account.platform);
                    const connected = account.status === 'connected';
                    const disabled = !phase1 || !connected || submitting;
                    const errors = accountErrors[account.id] || [];
                    const note = !phase1 ? 'Coming soon' : !connected ? 'Reconnect in Social accounts' : account.platform === postPlatform ? planNote : '';
                    const errorId = `${fileId}-account-${account.id}-error`;
                    return (
                        <li key={account.id} className={`ls-publish-account${!phase1 || !connected ? ' is-disabled' : ''}${errors.length ? ' is-invalid' : ''}`}>
                            <label className="ls-publish-account-row">
                                <input
                                    type="checkbox"
                                    checked={selected.includes(account.id)}
                                    disabled={disabled}
                                    onChange={(e) => toggleAccount(account.id, e.target.checked)}
                                    aria-describedby={errors.length ? errorId : undefined}
                                    aria-invalid={errors.length ? true : undefined}
                                />
                                <span className="ls-publish-glyph" aria-hidden="true"><Icon name={platformIcon(account.platform)} size={18} /></span>
                                <span className="ls-publish-account-text">
                                    <span className="ls-publish-handle">{accountName(account)}</span>
                                    <span className="ls-publish-account-sub">{platformLabel(account.platform)}</span>
                                </span>
                                {note && <span className={`ls-publish-account-note${note === planNote ? ' is-planned' : ''}`}>{note}</span>}
                            </label>
                            {errors.length > 0 && (
                                <ul className="ls-publish-account-errors" id={errorId} role="alert">
                                    {errors.map((message, index) => <li key={index}><Icon name="error" size={14} />{message}</li>)}
                                </ul>
                            )}
                        </li>
                    );
                })}
            </ul>
        );
    };

    const renderCounters = () => (
        <span className="ls-publish-counts">
            {selectedPlatforms.length === 0 ? (
                <span className="ls-publish-count">{text.trim().length.toLocaleString()} characters</span>
            ) : selectedPlatforms.map(platform => {
                const limit = capsFor(platform).textLimit;
                const length = lengthFor(platform);
                const over = limit && length > limit;
                return (
                    <span
                        key={platform}
                        className={`ls-publish-count${over ? ' is-over' : ''}`}
                        title={linkFor(platform) ? 'Includes room for the tracked link' : undefined}
                    >
                        {platformLabel(platform)} {length.toLocaleString()}{limit ? ` / ${limit.toLocaleString()}` : ''}
                    </span>
                );
            })}
        </span>
    );

    const renderTile = (item, index) => {
        const label = item.status === 'error' ? `${item.name}: ${item.error}` : item.name;
        return (
            <li key={item.key} className={`ls-publish-tile is-${item.status}`} title={label}>
                {item.kind === 'image'
                    ? <img src={item.previewUrl} alt="" />
                    : <video src={item.previewUrl} muted playsInline preload="metadata" aria-hidden="true" />}
                {item.kind === 'video' && <span className="ls-publish-tile-kind"><Icon name="movie" size={14} /></span>}
                {(item.status === 'preparing' || item.status === 'uploading') && (
                    <span className="ls-publish-tile-busy" role="status" aria-label={`Uploading ${item.name}`}>
                        <span className="ls-spinner" />
                        <span className="ls-publish-progress"><span style={{ transform: `scaleX(${item.status === 'uploading' ? item.progress : 0.04})` }} /></span>
                    </span>
                )}
                {item.status === 'error' && (
                    <span className="ls-publish-tile-error"><Icon name="error" size={22} /></span>
                )}
                <button type="button" className="ls-publish-tile-btn is-remove" onClick={() => removeItem(item)} aria-label={`Remove ${item.name}`} disabled={submitting}>
                    <Icon name="close" size={14} />
                </button>
                {index > 0 && media.length > 1 && (
                    <button type="button" className="ls-publish-tile-btn is-move" onClick={() => moveItem(index)} aria-label={`Move ${item.name} earlier`} disabled={submitting}>
                        <Icon name="arrow_back" size={14} />
                    </button>
                )}
            </li>
        );
    };

    const mediaErrors = media.filter(item => item.status === 'error');
    const mediaNeedText = selectedPlatforms.filter(platform => capsFor(platform).needsMedia).map(platformLabel);
    const mediaLine = mediaErrors.length
        ? { tone: 'is-error', text: `${mediaErrors[0].name} didn't upload: ${mediaErrors[0].error}` }
        : mediaNotice
            ? { tone: 'is-error', text: mediaNotice }
            : uploading
                ? { tone: '', text: 'Uploading… you can keep editing.' }
                : mediaNeedText.length && !readyMedia.length
                    ? { tone: needsMediaNames.length && tried ? 'is-error' : '', text: `${listNames(mediaNeedText)} ${mediaNeedText.length === 1 ? 'needs' : 'need'} an image or video.` }
                    : { tone: '', text: 'Images are sent as JPEG (up to 2048px). One video, or up to 10 images.' };

    const pinterestBoards = pinterestAccount ? boards[pinterestAccount.id] : null;
    const zone = browserZone();
    const submitLabel = when === 'now' ? 'Publish now' : 'Schedule';
    const noAccounts = load.status === 'ready' && !usable.length;
    const canSubmit = usable.length > 0 && load.status === 'ready';

    return createPortal(
        <div className="ls-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && requestClose()}>
            <div
                className="ls-modal ls-publish-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-busy={load.status === 'loading' || submitting}
                tabIndex={-1}
                ref={modalRef}
            >
                <div className="ls-publish-head">
                    <div className="ls-publish-heading">
                        <div className="ls-eyebrow"><Icon name="send" size={16} />Publish</div>
                        <h2 className="ls-publish-title" id={titleId}>{post.theme || post.hook || 'Planned post'}</h2>
                    </div>
                    <button type="button" className="ls-btn ls-btn-icon" onClick={requestClose} aria-label="Close" disabled={submitting}>
                        <Icon name="close" />
                    </button>
                </div>

                <section className="ls-publish-section" aria-labelledby={`${fileId}-accounts`}>
                    <div className="ls-publish-section-head">
                        <span className="ls-label" id={`${fileId}-accounts`}>Post to</span>
                        {usable.length > 0 && <Link className="ls-link" to="/dashboard/channels">Manage accounts</Link>}
                    </div>
                    {renderAccounts()}
                    {load.status === 'ready' && comingSoon.length > 0 && (
                        <div className="ls-publish-soon">
                            <span className="ls-publish-soon-label">Coming soon</span>
                            {comingSoon.map(id => (
                                <span key={id} className="ls-chip ls-publish-soon-chip"><Icon name={platformIcon(id)} size={14} />{platformLabel(id)}</span>
                            ))}
                        </div>
                    )}
                </section>

                {!noAccounts && (
                    <>
                    <section className="ls-publish-section">
                        <div className="ls-publish-section-head">
                            <label className="ls-label" htmlFor={textId}>Text</label>
                            {renderCounters()}
                        </div>
                        <textarea
                            id={textId}
                            className="ls-textarea is-compact ls-publish-text"
                            rows={6}
                            maxLength={10000}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            disabled={submitting}
                        />
                    </section>

                    <section className="ls-publish-section" aria-labelledby={`${fileId}-media`}>
                        <div className="ls-publish-section-head">
                            <span className="ls-label" id={`${fileId}-media`}>Media</span>
                            {media.length > 0 && <span className="ls-publish-count">{media.length} {media.length === 1 ? 'file' : 'files'}</span>}
                        </div>
                        <input
                            ref={fileRef}
                            id={fileId}
                            type="file"
                            accept={ACCEPT}
                            multiple
                            className="ls-publish-file"
                            onChange={(e) => {
                                addFiles(e.target.files);
                                e.target.value = '';
                            }}
                            tabIndex={-1}
                            aria-hidden="true"
                        />
                        <div
                            className={`ls-publish-media${dragOver ? ' is-over' : ''}`}
                            onDragOver={(e) => {
                                e.preventDefault();
                                if (!submitting) setDragOver(true);
                            }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragOver(false);
                                if (!submitting) addFiles(e.dataTransfer.files);
                            }}
                        >
                            {media.length === 0 ? (
                                <button type="button" className="ls-dropzone ls-publish-drop ls-publish-add" onClick={() => fileRef.current?.click()} disabled={submitting}>
                                    <Icon name="add_photo_alternate" size={24} />
                                    <span><b>Add images or a video</b> or drop them here</span>
                                    <span className="ls-publish-drop-hint">JPEG, PNG or WebP up to 8 MB · MP4 or MOV up to 300 MB</span>
                                </button>
                            ) : (
                                <ul className="ls-publish-tiles">
                                    {media.map(renderTile)}
                                    {!hasVideoMedia && media.length < MAX_FILES && (
                                        <li className="ls-publish-tile is-add">
                                            <button type="button" className="ls-publish-add" onClick={() => fileRef.current?.click()} disabled={submitting} aria-label="Add more images">
                                                <Icon name="add" size={22} />
                                            </button>
                                        </li>
                                    )}
                                </ul>
                            )}
                        </div>
                        <p className={`ls-publish-line ${mediaLine.tone}`} role={mediaLine.tone ? 'alert' : undefined}>{mediaLine.text}</p>
                    </section>

                    {pinterestAccount && (
                        <section className="ls-publish-section ls-enter">
                            <div className="ls-form-grid ls-publish-grid">
                                <div className="ls-field">
                                    <label className="ls-label" htmlFor={`${fileId}-board`}>Pinterest board</label>
                                    {pinterestBoards?.status === 'error' ? (
                                        <div className="ls-publish-board-error" role="alert">
                                            <span>{pinterestBoards.error}</span>
                                            <button type="button" className="ls-link" onClick={() => loadBoards(pinterestAccount.id)}>Try again</button>
                                        </div>
                                    ) : (
                                        <select
                                            id={`${fileId}-board`}
                                            className="ls-select"
                                            value={boardId}
                                            onChange={(e) => setBoardId(e.target.value)}
                                            disabled={pinterestBoards?.status !== 'ready' || !pinterestBoards.list.length || submitting}
                                        >
                                            {pinterestBoards?.status !== 'ready' && <option value="">Loading boards…</option>}
                                            {pinterestBoards?.status === 'ready' && !pinterestBoards.list.length && <option value="">No boards yet: create one on Pinterest</option>}
                                            {(pinterestBoards?.list || []).map(board => <option key={board.id} value={board.id}>{board.name}</option>)}
                                        </select>
                                    )}
                                </div>
                                <div className="ls-field">
                                    <label className="ls-label" htmlFor={`${fileId}-pin-title`}>Pin title</label>
                                    <input
                                        id={`${fileId}-pin-title`}
                                        className="ls-input"
                                        type="text"
                                        value={pinTitle}
                                        maxLength={100}
                                        onChange={(e) => setPinTitle(e.target.value)}
                                        disabled={submitting}
                                    />
                                </div>
                            </div>
                        </section>
                    )}

                    <section className="ls-publish-section">
                        <label className={`ls-check ls-publish-link-check${canAddLink ? '' : ' is-disabled'}`}>
                            <input type="checkbox" checked={addLink && canAddLink} disabled={!canAddLink || submitting} onChange={(e) => setAddLink(e.target.checked)} aria-describedby={`${fileId}-link-hint`} />
                            Add tracked link
                        </label>
                        <span className="ls-hint ls-publish-hint" id={`${fileId}-link-hint`}>
                            {canAddLink
                                ? `A short link${linkDestination ? ` to ${linkDestination.replace(/^https?:\/\//, '').replace(/\/$/, '')}` : ''} goes at the end, so clicks are counted. Instagram captions can't hold links: put it in your bio there.`
                                : 'Set a link destination in plan settings to add a tracked link.'}
                        </span>
                    </section>

                    <section className="ls-publish-section" aria-labelledby={`${fileId}-when`}>
                        <span className="ls-label" id={`${fileId}-when`}>When</span>
                        <div className="ls-publish-when">
                            <div className="ls-segmented" role="group" aria-labelledby={`${fileId}-when`}>
                                <button type="button" aria-pressed={when === 'now'} onClick={() => setWhen('now')} disabled={submitting}>Post now</button>
                                <button type="button" aria-pressed={when === 'schedule'} onClick={() => setWhen('schedule')} disabled={submitting}>Schedule</button>
                            </div>
                            <input
                                type="datetime-local"
                                className={`ls-input is-compact ls-publish-datetime${tried && (scheduleInvalid || schedulePast) ? ' is-invalid' : ''}`}
                                value={scheduleValue}
                                min={toLocalInput(new Date())}
                                onChange={(e) => setScheduleValue(e.target.value)}
                                disabled={when !== 'schedule' || submitting}
                                aria-label="Date and time"
                                aria-describedby={`${fileId}-when-hint`}
                            />
                        </div>
                        <span className="ls-hint ls-publish-hint" id={`${fileId}-when-hint`}>
                            {when === 'now'
                                ? 'Goes out within a minute or two.'
                                : `${scheduleInvalid ? 'Pick a date and time' : formatWhen(scheduledDate.toISOString())}${zone ? ` · ${zone.replace(/_/g, ' ')}` : ''}${planned && toLocalInput(planned) !== scheduleValue ? ` · planned for ${formatWhen(planned.toISOString())}` : ''}`}
                        </span>
                    </section>

                    {(error || (tried && issues.length > 0)) && (
                        <Alert severity="error" className="ls-publish-alert">
                            {error || (issues.length === 1 ? issues[0] : (
                                <ul className="ls-publish-issues">{issues.map(issue => <li key={issue}>{issue}</li>)}</ul>
                            ))}
                        </Alert>
                    )}
                    </>
                )}

                <div className="ls-publish-foot">
                    <span className="ls-publish-summary">
                        {noAccounts ? '' : selected.length ? `${selected.length} ${selected.length === 1 ? 'account' : 'accounts'}` : 'No account picked'}
                        {readyMedia.length ? ` · ${readyMedia.length} ${readyMedia.length === 1 ? 'file' : 'files'}` : ''}
                    </span>
                    <div className="ls-publish-foot-actions">
                        <button type="button" className="ls-btn ls-btn-ghost" onClick={requestClose} disabled={submitting}>{noAccounts ? 'Close' : 'Cancel'}</button>
                        {!noAccounts && (
                            <button
                                type="button"
                                className="ls-btn ls-btn-primary ls-publish-submit"
                                onClick={submit}
                                disabled={!canSubmit || busy}
                                title={uploading ? 'Wait for the uploads to finish' : undefined}
                            >
                                {submitting || uploading ? <span className="ls-spinner" /> : <Icon name={when === 'now' ? 'send' : 'schedule_send'} size={18} />}
                                {submitLabel}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default PublishModal;
